import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageHeader } from '../components/PageHeader';
import { Leaf } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const seasonColors = {
    spring: 'bg-green-100 text-green-700',
    summer: 'bg-yellow-100 text-yellow-700',
    autumn: 'bg-orange-100 text-orange-700',
    winter: 'bg-blue-100 text-blue-700',
    all_year: 'bg-primary/10 text-primary',
    Spring: 'bg-green-100 text-green-700',
    Summer: 'bg-yellow-100 text-yellow-700',
    Autumn: 'bg-orange-100 text-orange-700',
    Winter: 'bg-blue-100 text-blue-700',
    'All Year': 'bg-primary/10 text-primary',
};

const seasonLabels = {
    spring: 'Spring',
    summer: 'Summer',
    autumn: 'Autumn',
    winter: 'Winter',
    all_year: 'All Year',
};

export function TipDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tip, setTip] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTip();
    }, [id]);

    const fetchTip = async () => {
        try {
            const response = await axios.get(`${API}/tips/${id}`);
            setTip(response.data);
        } catch (error) {
            console.error('Error fetching tip:', error);
            navigate('/tips');
        } finally {
            setLoading(false);
        }
    };

    // Simple markdown-like rendering
    const renderContent = (content) => {
        return content.split('\n\n').map((paragraph, pIndex) => {
            // Check if it's a heading
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return (
                    <h3 key={pIndex} className="text-lg font-bold text-foreground mt-6 mb-3">
                        {paragraph.replace(/\*\*/g, '')}
                    </h3>
                );
            }
            
            // Check if it's a list
            if (paragraph.includes('\n-')) {
                const lines = paragraph.split('\n');
                return (
                    <div key={pIndex}>
                        {lines[0] && <p className="font-bold text-foreground mb-2">{lines[0].replace(/\*\*/g, '')}</p>}
                        <ul className="space-y-2 ml-4">
                            {lines.slice(1).filter(l => l.startsWith('-')).map((line, lIndex) => (
                                <li key={lIndex} className="text-muted-foreground flex items-start gap-2">
                                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                                    <span>{line.replace(/^-\s*/, '')}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            }

            // Regular paragraph
            return (
                <p key={pIndex} className="text-muted-foreground leading-relaxed">
                    {paragraph.split('**').map((part, i) => 
                        i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                    )}
                </p>
            );
        });
    };

    if (loading) {
        return (
            <div className="page-container">
                <PageHeader title="Garden Tip" showBack />
                <div className="flex justify-center items-center py-20">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (!tip) {
        return null;
    }

    const seasonKey = tip.season || tip.season_tag || 'all_year';
    const content = tip.description || tip.content || '';

    return (
        <div className="page-container" data-testid="tip-detail-page">
            <PageHeader title="Garden Tip" showBack />
            
            <div className="px-4 py-4">
                {/* Header */}
                <div className="mb-6">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-3 ${
                        seasonColors[seasonKey] || seasonColors['all_year']
                    }`}>
                        {seasonLabels[seasonKey] || seasonKey}
                    </span>
                    <h1 className="text-2xl font-bold text-foreground">{tip.title}</h1>
                </div>

                {/* Content */}
                <div className="card-base space-y-4" data-testid="tip-content">
                    {renderContent(content)}
                </div>

                {/* Footer */}
                <div className="mt-8 card-base bg-primary/5">
                    <div className="flex items-center gap-3">
                        <Leaf className="w-8 h-8 text-primary" />
                        <div>
                            <p className="font-bold text-foreground">Need more advice?</p>
                            <p className="text-muted-foreground text-sm">Our friendly staff are always happy to help in store</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TipDetailPage;
